<template>
  <table class="table table-hover" id="logTable">
    <thead>
      <tr>
        <th scope="col" class="col"></th>
        <th scope="col" class="col">Time</th>
        <th scope="col" class="col">Description</th>
        <th scope="col" class="col">Triggering user</th>
        <th scope="col" class="col">Users affected</th>
        <th scope="col" class="col">Groups affected</th>
      </tr>
    </thead>
    <tbody>
      <tr
        v-for="(event, index) in events"
        :key="event.timestamp"
        class="event-table-row"
        :class="{
          expanded: isEventExpanded(index),
        }"
      >
        <td>
          <button
            type="button"
            class="showHideButton btn btn-default btn-xs"
            style="margin: -1px 0"
            aria-label="open more details"
            @click="toggleEventExpansion(index)"
          >
            <span v-if="!isEventExpanded(index)" class="glyphicon glyphicon-plus text-muted"></span>
            <span v-else class="glyphicon glyphicon-minus text-muted"></span>
          </button>
        </td>
        <td>
          {{ getFormattedDate(event.timestamp) }}
        </td>
        <td>
          <a
            v-if="event.extraProperties.find((p) => p.key === 'ReportId')"
            target="_blank"
            rel="noopener noreferrer"
            :href="getReportLink(event)"
            >{{ event.displayName }}</a
          >
          <span v-else>{{ event.displayName }}</span>

          <div class="hidden-on-collapse">
            <div>
              <div>
                <span class="event-property-key">Type:</span>
                <br />
                <span>{{ event.type }}</span>
              </div>
              <div v-for="prop in event.extraProperties" :key="prop.key">
                <span class="event-property-key">{{ prop.key }}:</span>
                <br />
                <span>{{ prop.value !== null ? prop.value.toString() : "null" }}</span>
              </div>
            </div>
          </div>
        </td>
        <td>
          <a
            :href="`/admin/user/details/${
              event.triggeredByUser ? event.triggeredByUser.userId : ''
            }`"
            target="_blank"
            rel="noopener noreferrer"
            >{{ event.triggeredByUser ? event.triggeredByUser.userEmail : "" }}</a
          >
        </td>
        <td>
          <span v-for="(user, index) in event.usersAffected" :key="user.userId">
            <a
              :href="`/admin/user/details/${user.userId}`"
              target="_blank"
              rel="noopener noreferrer"
              >{{ user.userEmail }}</a
            >
            <span v-if="index + 1 < event.usersAffected.length">, </span>
          </span>
        </td>
        <td>
          <span v-for="(group, index) in event.groupsAffected" :key="group.groupId">
            <a
              :href="`/admin/group/details/${group.groupId}`"
              target="_blank"
              rel="noopener noreferrer"
              >{{ group.groupName }}</a
            >
            <span v-if="index + 1 < event.groupsAffected.length">, </span>
          </span>
        </td>
      </tr>
    </tbody>
  </table>
</template>

<script setup lang="ts">
import { ref, triggerRef } from "vue";
import moment from "dayjs";
import { EventLogEntryApiModel } from "../../api-models";
import resolveReportLink from "./report-link-resolver";

const props = defineProps<{
  events: EventLogEntryApiModel[];
}>();

const expandedEvents = ref<Record<string, boolean>>({});

function getReportLink(event: EventLogEntryApiModel): string {
  const reportUrl = resolveReportLink(event.type);
  const reportId = event.extraProperties.find((p) => p.key === "ReportId")?.value ?? "";
  return reportUrl + reportId;
}

function isEventExpanded(index: number): boolean {
  return expandedEvents.value[index];
}

function getFormattedDate(timestamp: string): string {
  return moment.utc(timestamp).local().format("D/M/YYYY H:mm:ss");
}

function toggleEventExpansion(index: number): void {
  if (typeof expandedEvents.value[index] === "undefined") {
    expandedEvents.value[index] = false;
  }
  expandedEvents.value[index] = !expandedEvents.value[index];
  triggerRef(expandedEvents);
}
</script>

<style scoped lang="less">
@import (reference) "../../styles/bootstrap/variables.less";

@media (max-width: @screen-lg-desktop) {
  table {
    display: block;
    overflow-x: scroll;
    white-space: nowrap;
  }
}

.event-table-row:not(.expanded) .hidden-on-collapse {
  display: none;
}

.hidden-on-collapse {
  margin-top: 5px;
}

.event-property-key {
  font-weight: bold;
}

.dt-buttons {
  position: relative;
  top: 36px;
}
</style>
